import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from '../HashRouter';
import type { VertexSetWithVertices } from '../../types';
import * as api from '../../services/api';
import { StatsBar } from '../shared/StatsBar';
import { GlobeIcon, DeleteIcon } from '../icons';
import { OutlinedButton } from '../shared/OutlinedButton';

export function VertexSetsPage() {
  const [sets, setSets] = useState<VertexSetWithVertices[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [newSetDescription, setNewSetDescription] = useState('');

  useEffect(() => {
    loadSets();
  }, []);

  async function loadSets() {
    try {
      setLoading(true);
      setError(null);
      const allSets = await api.fetchAllVertexSets();
      // Sort by most recent vertex addition (empty sets fall back to createdAt).
      const latestActivity = (s: VertexSetWithVertices) =>
        s.vertices.length === 0
          ? new Date(s.vertexSet.createdAt).getTime()
          : s.vertices.reduce((max, v) => Math.max(max, new Date(v.createdAt).getTime()), 0);
      allSets.sort((a, b) => latestActivity(b) - latestActivity(a));
      setSets(allSets);
    } catch (err) {
      console.error('Failed to load vertex sets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load vertex sets');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateSet(e: Event) {
    e.preventDefault();
    if (!newSetName.trim()) return;

    try {
      const newSet = await api.createVertexSet({
        name: newSetName.trim(),
        description: newSetDescription.trim() || null,
      });
      setSets((prev) => [newSet, ...prev]);
      setNewSetName('');
      setNewSetDescription('');
      setCreating(false);
    } catch (err) {
      alert(`Failed to create set: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleDeleteSet(setId: string, setName: string) {
    if (!confirm(`Delete set "${setName}"? This cannot be undone.`)) return;

    try {
      await api.deleteVertexSet(setId);
      setSets((prev) => prev.filter((s) => s.vertexSet.id !== setId));
    } catch (err) {
      alert(`Failed to delete set: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function formatDate(isoDate: string): string {
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  if (loading) {
    return (
      <div class="loading" style="padding: 60px 20px; color: #6c757d; font-size: 18px;">
        Loading vertex sets...
      </div>
    );
  }

  if (error) {
    return (
      <div class="error-message" style="padding: 20px; color: #dc3545;">
        {error}
      </div>
    );
  }

  return (
    <div class="vertex-sets-page">
      <StatsBar
        count={sets.length}
        singular="vertex set"
        plural="vertex sets"
        actions={
          !creating && (
            <OutlinedButton onClick={() => setCreating(true)}>
              + New Set
            </OutlinedButton>
          )
        }
      />

      {creating && (
        <form class="vertex-set-create-form" onSubmit={handleCreateSet}>
          <input
            type="text"
            placeholder="Set name"
            value={newSetName}
            onInput={(e) => setNewSetName((e.target as HTMLInputElement).value)}
            autoFocus
          />
          <textarea
            placeholder="Description (optional)"
            value={newSetDescription}
            onInput={(e) => setNewSetDescription((e.target as HTMLTextAreaElement).value)}
            rows={2}
          />
          <div class="vertex-set-create-actions">
            <button type="button" class="btn-cancel" onClick={() => { setCreating(false); setNewSetName(''); setNewSetDescription(''); }}>
              Cancel
            </button>
            <button type="submit" class="btn-confirm" disabled={!newSetName.trim()}>
              Create
            </button>
          </div>
        </form>
      )}

      {sets.length === 0 && !creating ? (
        <div class="empty-state" style="padding: 60px 20px; text-align: center; color: #777;">
          <p>No vertex sets yet.</p>
          <p class="subtitle">Create a set to organize your vertices into collections.</p>
        </div>
      ) : (
        <div class="vertex-sets-list">
          {sets.map((setData) => (
            <article
              key={setData.vertexSet.id}
              class="vertex-set-card"
              onClick={() => route(`/vertex-sets/${setData.vertexSet.id}`)}
            >
              <div class="vertex-set-main">
                <h3 class="vertex-set-name">
                  {setData.vertexSet.name}
                  {setData.shareId && (
<GlobeIcon size={14} className="vertex-set-shared-icon" title="Shared publicly" />
                  )}
                </h3>
                {setData.vertexSet.description && (
                  <p class="vertex-set-description">{setData.vertexSet.description}</p>
                )}
                <p class="vertex-set-meta">
                  {setData.vertices.length} {setData.vertices.length === 1 ? 'vertex' : 'vertices'}
                  <span class="vertex-set-updated">
                    {' '}· Updated {formatDate(setData.vertexSet.updatedAt)}
                  </span>
                </p>
              </div>
              <div class="vertex-set-actions">
                <button
                  class="vertex-set-delete-btn"
                  title="Delete set"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSet(setData.vertexSet.id, setData.vertexSet.name);
                  }}
                >
<DeleteIcon />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
